import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/components/Reveal";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "100+", label: "Clients" },
  { value: "150+", label: "Manufacturing Partners" },
  { value: "04", label: "Core Product Verticals" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yOrb = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-white"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 hero-grid-bg" />
      <motion.div style={{ y: yOrb }} className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 right-[-10%] w-[560px] h-[560px] rounded-full bg-[#2E7FE0]/14 blur-[140px] animate-pulse-glow" />
        <div className="absolute bottom-[-20%] left-[-8%] w-[480px] h-[480px] rounded-full bg-[#2E7FE0]/10 blur-[120px]" />
      </motion.div>

      <motion.div
        style={{ y: yText, opacity: fade }}
        className="relative z-10 max-w-[1440px] mx-auto w-full px-6 lg:px-12 pt-32 pb-16"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15 }}
          className="flex items-center gap-4 mb-10"
        >
          <span className="w-2 h-2 rounded-full bg-[#2E7FE0] animate-pulse-glow" />
          <span className="font-mono2 text-[11px] sm:text-xs uppercase tracking-[0.35em] text-[#54678A]" data-testid="hero-eyebrow">
            NexGen Sourcing Private Limited
          </span>
        </motion.div>

        <h1 className="font-display font-bold tracking-tight leading-[0.95] text-[13.5vw] sm:text-[11vw] lg:text-[8.5vw]" data-testid="hero-headline">
          <MaskedLine delay={0.35} className="text-[#0A1B33]">
            Connect.
          </MaskedLine>
          <MaskedLine delay={0.5} className="text-[#2E7FE0]">
            Source.
          </MaskedLine>
          <MaskedLine delay={0.65} className="text-[#0A1B33]">
            Succeed.
          </MaskedLine>
        </h1>

        <div className="mt-12 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-xl text-[#54678A] text-base sm:text-lg leading-relaxed"
            data-testid="hero-subcopy"
          >
            Your sourcing and export partner in India — connecting you to verified Indian
            manufacturers across textiles, food, home and industrial products, with quality
            controlled at source and export executed end to end.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToSection("#contact")}
              className="group inline-flex items-center gap-3 bg-[#2E7FE0] hover:bg-[#4a93eb] text-white font-display font-medium px-8 py-4 rounded-full transition-colors duration-300"
              data-testid="hero-cta-primary"
            >
              Start an Enquiry
              <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              onClick={() => scrollToSection("#process")}
              className="inline-flex items-center gap-3 border border-[#2E7FE0]/40 hover:border-[#2E7FE0] text-[#0A1B33] font-display font-medium px-8 py-4 rounded-full transition-colors duration-300"
              data-testid="hero-cta-secondary"
            >
              See How We Work
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-20 grid grid-cols-2 lg:grid-cols-3 border-t border-[#2E7FE0]/15"
          data-testid="hero-stats"
        >
          {STATS.map((s, i) => (
            <div
              key={s.label}
              className={`py-6 pr-6 ${i !== 0 ? "lg:border-l lg:border-[#2E7FE0]/15 lg:pl-8" : ""}`}
              data-testid={`hero-stat-${i}`}
            >
              <div className="font-display font-semibold text-3xl sm:text-4xl text-[#0A1B33]">{s.value}</div>
              <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#54678A] mt-2">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        onClick={() => scrollToSection("#about")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[#54678A] hover:text-[#0A1B33] transition-colors duration-300 hidden md:flex flex-col items-center gap-2"
        data-testid="hero-scroll-cue"
        aria-label="Scroll down"
      >
        <span className="font-mono2 text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.button>
    </section>
  );
};
